import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '@/lib/constants';
import { useVenueContext } from '@/providers/VenueProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useCreditBalance } from '@/api/hooks/useCredit';
import { usePreviewBooking, useCreateBooking, useConfirmPayment } from '@/api/hooks/useBookings';
import { formatCurrency, formatLocalTime } from '@/lib/format';
import type { PreviewResponse } from '@/api/types';
import type { AppError } from '@/api/client';
import CheckoutAuth from '@/components/checkout/CheckoutAuth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, Clock, Shield, Loader2, AlertTriangle, Sparkles } from 'lucide-react';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

type CheckoutParams = {
  locationId: string;
  sportCode: string;
  sportName?: string;
  date: string;
  startTime: string;
  duration: number;
  playerCount: number;
  unitId?: string;
  addons?: { addonId: string; quantity: number }[];
};

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function CheckoutForm({ params }: { params: CheckoutParams }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [authCompleted, setAuthCompleted] = useState(false);
  const [useCredits, setUseCredits] = useState(true);
  const idempotencyKeyRef = useRef(crypto.randomUUID());

  const previewMutation = usePreviewBooking();
  const createBookingMutation = useCreateBooking();
  const confirmPaymentMutation = useConfirmPayment();
  const { data: creditData } = useCreditBalance(isAuthenticated || authCompleted);

  const showPayment = isAuthenticated || authCompleted;
  const creditBalance = (isAuthenticated || authCompleted) ? (creditData?.balance || 0) : 0;

  // Fetch preview on mount (public endpoint, no auth needed)
  useEffect(() => {
    previewMutation.mutate(
      {
        locationId: params.locationId,
        sportCode: params.sportCode,
        unitId: params.unitId,
        startTime: params.startTime,
        duration: params.duration,
        playerCount: params.playerCount,
        addons: params.addons,
      },
      {
        onSuccess: (data) => {
          setPreview(data);
          setError(null);
        },
        onError: (err) => {
          const appErr = err as unknown as AppError;
          if (appErr.code === 'SLOT_UNAVAILABLE') {
            setError(t('booking.slot_taken'));
          } else {
            setError(appErr.message || t('common.error'));
          }
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!preview?.expiresAt) return;
    const expiresAt = new Date(preview.expiresAt).getTime();

    const tick = () => {
      const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setTimeRemaining(remaining);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [preview?.expiresAt]);

  const handleAuthSuccess = useCallback(() => {
    setAuthCompleted(true);
    queryClient.invalidateQueries({ queryKey: ['creditBalance'] });
  }, [queryClient]);

  const creditToApply = preview && useCredits ? Math.min(creditBalance, preview.pricing.total) : 0;
  const amountDue = preview ? preview.pricing.total - creditToApply : 0;

  const handlePay = useCallback(async () => {
    if (!preview) return;

    setError(null);
    setIsProcessing(true);

    try {
      // Fully covered by credit — skip Stripe entirely
      if (amountDue <= 0) {
        const response = await createBookingMutation.mutateAsync({
          request: {
            locationId: params.locationId,
            sportCode: params.sportCode,
            unitId: params.unitId,
            startTime: params.startTime,
            duration: params.duration,
            playerCount: params.playerCount,
            addons: params.addons,
            useCredits,
          },
          idempotencyKey: idempotencyKeyRef.current,
        });

        sessionStorage.removeItem('checkoutParams');
        navigate(`/booking/${response.booking.id}/confirmed`, { replace: true });
        return;
      }

      // Stripe payment flow
      if (!stripe || !elements) return;

      // 1. Create Stripe PaymentMethod from card
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) return;

      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        setError(stripeError.message || t('booking.card_declined'));
        setIsProcessing(false);
        return;
      }

      // 2. Create booking
      const response = await createBookingMutation.mutateAsync({
        request: {
          locationId: params.locationId,
          sportCode: params.sportCode,
          unitId: params.unitId,
          startTime: params.startTime,
          duration: params.duration,
          playerCount: params.playerCount,
          paymentMethodId: paymentMethod.id,
          addons: params.addons,
          useCredits,
        },
        idempotencyKey: idempotencyKeyRef.current,
      });

      const { booking, payment } = response;

      // 3. Handle 3D Secure if needed
      if (payment.status === 'REQUIRES_ACTION' && payment.clientSecret) {
        const { error: actionError } = await stripe.handleCardAction(payment.clientSecret);
        if (actionError) {
          setError(actionError.message || t('booking.card_declined'));
          setIsProcessing(false);
          return;
        }

        await confirmPaymentMutation.mutateAsync({
          bookingId: booking.id,
          request: { paymentIntentId: payment.stripePaymentIntentId },
        });
      }

      // 4. Success — clear session storage and navigate to confirmation
      sessionStorage.removeItem('checkoutParams');
      navigate(`/booking/${booking.id}/confirmed`, { replace: true });
    } catch (err) {
      const appErr = err as AppError;
      if (appErr.code === 'SLOT_UNAVAILABLE' || appErr.code === 'UNIT_UNAVAILABLE') {
        setError(t('booking.slot_taken'));
      } else if (appErr.code === 'PAYMENT_FAILED') {
        setError(t('booking.card_declined'));
      } else {
        setError(appErr.message || t('common.error'));
      }
      // Generate new idempotency key for retry
      idempotencyKeyRef.current = crypto.randomUUID();
      setIsProcessing(false);
    }
  }, [stripe, elements, preview, params, amountDue, navigate, t, createBookingMutation, confirmPaymentMutation]);

  const expired = timeRemaining !== null && timeRemaining <= 0;

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-6">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        {t('common.back')}
      </button>

      <h1 className="text-2xl font-semibold">{t('booking.checkout_title')}</h1>

      {/* Loading skeleton */}
      {(previewMutation.isPending || previewMutation.status === 'idle') && !error && (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {preview && (
        <>
          {/* ===== TOP SECTION: Always visible ===== */}

          {/* Booking summary — no labels, self-explanatory */}
          <Card>
            <CardContent className="space-y-2 p-6 text-center">
              <p className="text-lg font-semibold">{t(`sport_${params.sportCode}`, { defaultValue: params.sportName || preview.sportCode })}</p>
              <p className="text-xl font-medium">
                {formatLocalTime(preview.startTimeLocal, i18n.language)} → {formatLocalTime(preview.endTimeLocal, i18n.language)}
              </p>
              <p className="text-muted-foreground">
                {new Date(preview.startTime).toLocaleDateString(undefined, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
              <p className="text-sm text-muted-foreground">
                {preview.unitName || t('booking.auto_assigned')}
                {' · '}{preview.duration} min
                {preview.playerCount > 1 && ` · ${preview.playerCount} players`}
              </p>
            </CardContent>
          </Card>

          {/* Deposit notice */}
          {preview.depositAmount && preview.depositAmount > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">{t('booking.deposit_required', { amount: formatCurrency(preview.depositAmount, preview.pricing.currency, i18n.language) })}</p>
                <p className="text-amber-700">{t('booking.deposit_refund_notice')}</p>
              </div>
            </div>
          )}

          {/* Pricing breakdown */}
          <Card className="border-l-4 border-l-primary">
            <CardContent className="space-y-3 pt-6">
              <div className="flex justify-between">
                <span>{t('booking.unit_price', { duration: preview.duration })}</span>
                <span>{formatCurrency(
                  preview.pricing.subtotal + (preview.pricing.discount || 0),
                  preview.pricing.currency
                , i18n.language)}</span>
              </div>
              {(preview.pricing.discount ?? 0) > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{t('booking.vip_discount')}</span>
                  <span>-{formatCurrency(preview.pricing.discount!, preview.pricing.currency, i18n.language)}</span>
                </div>
              )}
              {preview.pricing.addonTotal > 0 && (
                <div className="flex justify-between">
                  <span>{t('booking.addon_total')}</span>
                  <span>{formatCurrency(preview.pricing.addonTotal, preview.pricing.currency, i18n.language)}</span>
                </div>
              )}
              <div className="flex justify-between text-muted-foreground text-sm">
                <span>{t('booking.platform_fee')}</span>
                <span>{formatCurrency(preview.pricing.platformFee, preview.pricing.currency, i18n.language)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground text-sm">
                <span>{t('booking.tax')}</span>
                <span>{formatCurrency(preview.pricing.tax, preview.pricing.currency, i18n.language)}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-lg font-semibold">
                <span>{t('booking.total')}</span>
                <span>{formatCurrency(preview.pricing.total, preview.pricing.currency, i18n.language)}</span>
              </div>
              {preview.depositAmount && preview.depositAmount > 0 && (
                <>
                  <Separator />
                  <div className="flex justify-between text-amber-700">
                    <span>{t('booking.deposit')}</span>
                    <span>{formatCurrency(preview.depositAmount, preview.pricing.currency, i18n.language)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{t('booking.deposit_refund_notice')}</p>
                </>
              )}
              {creditBalance > 0 && showPayment && (
                <>
                  <Separator />
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm">{t('credit.use_credits')} ({formatCurrency(creditBalance, preview.pricing.currency, i18n.language)})</span>
                    <input type="checkbox" checked={useCredits} onChange={(e) => setUseCredits(e.target.checked)}
                      className="h-4 w-4 rounded border-input accent-primary" />
                  </label>
                </>
              )}
              {creditToApply > 0 && (
                <>
                  <Separator />
                  <div className="flex justify-between text-green-600">
                    <span className="whitespace-nowrap">{t('credit.credit_applied')}</span>
                    <span className="font-mono">-{formatCurrency(creditToApply, preview.pricing.currency, i18n.language)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold">
                    <span className="whitespace-nowrap">{t('credit.amount_due')}</span>
                    <span className="font-mono">{formatCurrency(amountDue, preview.pricing.currency, i18n.language)}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Countdown timer */}
          {timeRemaining !== null && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              {expired
                ? t('booking.preview_expired')
                : t('booking.expires_in', { time: formatCountdown(timeRemaining) })}
            </div>
          )}

          {/* Cancellation policy */}
          {preview.cancellationPolicy && (
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <Shield className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t('booking.cancellation_policy')}</span>
            </div>
          )}

          {/* Credit upsell for zero-balance customers */}
          {showPayment && creditToApply === 0 && (
            <a href="/credits" className="flex items-center gap-1.5 text-xs text-primary hover:underline">
              <Sparkles className="h-3.5 w-3.5" />
              {t('credit.checkout_prompt')}
            </a>
          )}

          {/* ===== BOTTOM SECTION: Auth-dependent ===== */}

          {!showPayment ? (
            /* Inline login / register */
            <CheckoutAuth onAuthSuccess={handleAuthSuccess} />
          ) : (
            <>
              {/* Stripe card input — hidden when fully covered by credit */}
              {amountDue > 0 && (
              <Card>
                <CardContent className="pt-6">
                  <p className="mb-3 text-sm font-medium">{t('checkout.pay_with_card')}</p>
                  <div className="rounded-md border p-3">
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#1a1a1a',
                            '::placeholder': { color: '#a3a3a3' },
                          },
                        },
                        hidePostalCode: true,
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Pay button */}
              <Button
                className="w-full whitespace-nowrap min-w-[200px]"
                size="lg"
                disabled={isProcessing || expired || (amountDue > 0 && !stripe)}
                onClick={handlePay}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('booking.processing_payment')}
                  </>
                ) : amountDue <= 0 ? (
                  t('credit.pay_with_credit')
                ) : (
                  t('booking.pay_button', {
                    amount: formatCurrency(amountDue, preview.pricing.currency, i18n.language),
                  })
                )}
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { currentSlug } = useVenueContext();

  // Read params from navigation state or sessionStorage fallback
  const params: CheckoutParams | null = (() => {
    if (location.state) return location.state as CheckoutParams;
    const stored = sessionStorage.getItem('checkoutParams');
    if (stored) {
      try {
        return JSON.parse(stored) as CheckoutParams;
      } catch {
        return null;
      }
    }
    return null;
  })();

  useEffect(() => {
    if (!params) navigate(`/venue/${currentSlug}/book`, { replace: true });
  }, [params, navigate, currentSlug]);

  if (!params) return null;

  return (
    <Elements
      stripe={stripePromise}
      options={{ locale: i18n.language === 'fr' ? 'fr' : 'en' }}
    >
      <CheckoutForm params={params} />
    </Elements>
  );
}
